/* eslint-disable import/no-unresolved */
import * as React from "react";
import { useFormik } from "formik";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import {
  jointMedicalInitialValues,
  jointMedicalValidationSchema
} from "src/helpers/FormDetails/JointLifeAddendum";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { EInsurerType } from "src/helpers/FormDetails/ProposalDetails/interfaces";
import JointCovidAndNriReview from "src/components/FormDetails/Review/JointLife/JointCovidAndNri";
import AssignmentNotice from "src/components/FormDetails/Review/AssignmentNoticeReview/AssignmentNotice";
import JointLifeMedicalV2 from "src/components/FormDetails/Review/JointLife/JointLifeMedicalV2";
import MedicalAndLifeStyleV2 from "src/components/FormDetails/Review/MedicalAndLifeStyleV2";
import { useAppSelector } from "../../../utils/hooks/useAppSelector";
import MedicalAndLifestyle from "../../../shared/MedicalLifeStyle";
import GeneralDetails from "../../FormDetails/Review/GeneralReview/GeneralDetails";
import BankDetails from "../../FormDetails/Review/GeneralReview/BankDetails";
import Proposer from "../../FormDetails/Review/ProposerReview/Proposer";
import PrimaryReview from "../../FormDetails/Review/NomineeReview/PrimaryReview";
import PlanReview from "../../FormDetails/Review/LoanReview/PlanReview";
import PaymentMethodReview from "../../FormDetails/Review/LoanReview/OtherPolicyReview";
import PlanDetailsReview from "../../FormDetails/Review/LoanReview/PlanDetailsReview";
import PremiumDetailsReview from "../../FormDetails/Review/LoanReview/PremiumDetailsReview";
import PremiumFundingReview from "../../FormDetails/Review/LoanReview/PremiumFundingReview";
import CovidAndNriReview from "../../FormDetails/Review/CovidAndNri/CovidAndNriReview";
import JointLifeMember from "../../FormDetails/Review/JointLife/JointLifeMember";
import SecondaryDetails from "../../FormDetails/Review/JointLife/SecondaryDetails";
import MorePolicyDetails from "../../FormDetails/Review/JointLife/MorePolicyDetails";
import {
  displayAdditionalQue,
  getQuestionnaireMapper
} from "../../../helpers/FormDetails/ProposalDetails/MedicalAndLifeStyle/mapper";
import {
  calcAgeFromToday,
  showNewVersionUW
} from "../../../helpers/FormDetails/ProposalDetails/resources";

interface Props {
  insurerType: EInsurerType;
}
function ProposalOtp({ insurerType }: Props) {
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const JointMedicalAndLifeStyle = useAppSelector((state) => state.jointLifeAddendum);
  const MedicalAndLifeStyle = useAppSelector((state) => state.medicalAndLifeStyle);
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  const loanDetails = useAppSelector((state) => state.loanDetails);
  const proposerGender = useAppSelector((state) => state.proposerDetails.general?.gender);
  const jointGender = useAppSelector((state) => state.jointLifeAddendum?.member?.gender);
  const jointLifeDetails = useAppSelector(
    (state) => state.jointLifeAddendum?.existingPolicy?.existingLifeCover
  );
  const productId = useAppSelector((state) => state.common?.productId)!;
  const partnerId = useAppSelector((state) => state.common?.leadDetails?.partnerId)!;

  const medicalQuestions = getQuestionnaireMapper(productId, partnerId);

  const sumAssured = useAppSelector((state) => state?.common?.leadDetails?.sumAssured);

  const primaryLifeDob = useAppSelector((state) => state?.proposerDetails?.general?.dob) || "";
  const toDisplayPrimaryLifeAdditionalQue = displayAdditionalQue(sumAssured, primaryLifeDob);

  const jointLifeDob = useAppSelector((state) => state?.jointLifeAddendum?.member?.dob) || "";
  const toDisplayJointLifeAdditionalQue = displayAdditionalQue(sumAssured, jointLifeDob);

  const isJointAddendumState = useAppSelector((state: any) => state.isJointAddendum.isJointState);
  const age = calcAgeFromToday(primaryLifeDob);

  const nomineeDetails = useAppSelector((state) => state.proposerDetails.nominee?.nominee || []);

  const assignmentStepperFlag = useAppSelector((state) => state.common?.assignmentStepperFlag);
  const nationality = useAppSelector((state) => state.proposerDetails?.general?.nationalityCode);
  const nationalityJointMember = useAppSelector(
    (state) => state.jointLifeAddendum?.member?.nationalityCode
  );
  const jointMembersData = useAppSelector((state) => state?.common?.leadDetails?.Members)?.filter(
    (e: any) => e?.isJointMember
  )[0];
  const primaryMembersData = useAppSelector((state) => state?.common?.leadDetails?.Members)?.filter(
    (e: any) => !e?.isJointMember
  )[0];

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: jointMedicalInitialValues,
    validationSchema: jointMedicalValidationSchema(productId, age, partnerId),
    onSubmit: () => {
      // console.log("Submitting Joint Life Addendum form!!", formik);
    }
  });

  return (
    <div>
      <div>
        <div className="mx-auto xl:w-290 w-auto bg-white pt-8.5 pb-10 rounded-xl">
          <div className="xl:pr-10 lg:pr-10 md:pr-10 sm:pr-10 pr-4  xl:pl-10.25 lg:pl-10.25 md:pl-10.25 sm:pl-10.25 pl-4">
            {[EInsurerType.ALL, EInsurerType.PRIMARY]?.includes(insurerType) && (
              <>
                <div className="border border-solid rounded-xl border-bianchiGreen">
                  <Accordion
                    className="!rounded-xl !shadow-none"
                    expanded={expanded === "panel1"}
                    onChange={handleChange("panel1")}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          className=" rounded-full border-2 border-coldMorning"
                          classes={{ root: "!p-1" }}
                        />
                      }
                    >
                      <Typography className="!text-base !font-bold !font-montserrat">
                        General Details
                      </Typography>
                    </AccordionSummary>
                    <hr className="border-solid border-black opacity-30" />
                    <AccordionDetails>
                      <Typography>
                        <GeneralDetails />
                        <BankDetails />
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </div>
                <div className=" mt-6 border border-solid rounded-xl border-bianchiGreen">
                  <Accordion
                    className="!rounded-xl !shadow-none"
                    expanded={expanded === "panel2"}
                    onChange={handleChange("panel2")}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          className=" rounded-full border-2 border-coldMorning"
                          classes={{ root: "!p-1" }}
                        />
                      }
                      aria-controls="panel2bh-content"
                      id="panel2bh-header"
                    >
                      <Typography className="!text-base !font-bold !font-montserrat">
                        Proposer Details
                      </Typography>
                    </AccordionSummary>
                    <hr className="border-solid border-black opacity-30" />
                    <AccordionDetails>
                      <Typography>
                        <Proposer />
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </div>
                {nomineeDetails[0].id && (
                  <div className="mt-6 border border-solid rounded-xl border-bianchiGreen">
                    <Accordion
                      className="!rounded-xl !shadow-none"
                      expanded={expanded === "panel3"}
                      onChange={handleChange("panel3")}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            className=" rounded-full border-2 border-coldMorning"
                            classes={{ root: "!p-1" }}
                          />
                        }
                        aria-controls="panel3bh-content"
                        id="panel3bh-header"
                      >
                        <Typography className="!text-base !font-bold">Nominee Details</Typography>
                      </AccordionSummary>
                      <hr className="border-solid border-black opacity-30" />
                      <AccordionDetails>
                        <Typography>
                          <PrimaryReview />
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}
                <div className="mt-6 border border-solid rounded-xl border-bianchiGreen">
                  <Accordion
                    className="!rounded-xl !shadow-none"
                    expanded={expanded === "panel4"}
                    onChange={handleChange("panel4")}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          className=" rounded-full border-2 border-coldMorning"
                          classes={{ root: "!p-1" }}
                        />
                      }
                      aria-controls="panel4bh-content"
                      id="panel5bh-header"
                    >
                      <Typography className="!text-base !font-bold">
                        Medical & Lifestyle Details
                      </Typography>
                    </AccordionSummary>
                    <hr className="border-solid border-black opacity-30" />
                    <AccordionDetails>
                      <Typography>
                        {showNewVersionUW(primaryMembersData) ? (
                          <MedicalAndLifeStyleV2 showButtons={false} />
                        ) : (
                          <MedicalAndLifestyle
                            questions={medicalQuestions}
                            displayAdditionalQue={toDisplayPrimaryLifeAdditionalQue}
                            label="Self"
                            isReview
                            jointMarginRequired
                            formValues={MedicalAndLifeStyle}
                            handleBlurs={formik.handleBlur}
                            handleChanges={formik.handleChange}
                            setFieldTouch={formik.setFieldTouched}
                            setFieldValue={formik.setFieldValue}
                            width="78"
                            gender={proposerGender}
                          />
                        )}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </div>
                {nationality !== "RES" && (
                  <div className="mt-6 border border-solid rounded-xl border-bianchiGreen">
                    <Accordion
                      className="!rounded-xl !shadow-none"
                      expanded={expanded === "panel5"}
                      onChange={handleChange("panel5")}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            className=" rounded-full border-2 border-coldMorning"
                            classes={{ root: "!p-1" }}
                          />
                        }
                        aria-controls="panel6bh-content"
                        id="panel5bh-header"
                      >
                        <Typography className="!text-base !font-bold">NRI Details</Typography>
                      </AccordionSummary>
                      <hr className="border-solid border-black opacity-30" />
                      <AccordionDetails>
                        <Typography>
                          <CovidAndNriReview />
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}
              </>
            )}
            {[EInsurerType.ALL, EInsurerType.JOINT]?.includes(insurerType) &&
              isJointAddendumState === "yes" && (
                <div className="mt-6 border border-solid rounded-xl border-bianchiGreen">
                  <Accordion
                    className="!rounded-xl !shadow-none"
                    expanded={expanded === "panel6"}
                    onChange={handleChange("panel6")}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          className=" rounded-full border-2 border-coldMorning"
                          classes={{ root: "!p-1" }}
                        />
                      }
                      aria-controls="panel6bh-content"
                      id="panel6bh-header"
                    >
                      <Typography className="!text-base !font-bold">Joint Life</Typography>
                    </AccordionSummary>
                    <hr className="border-solid border-black opacity-30" />
                    <AccordionDetails>
                      <Typography>
                        <div className="ml-10 mb-6">
                          <JointLifeMember />
                          <SecondaryDetails />
                          {jointLifeDetails && <MorePolicyDetails />}
                        </div>
                        <span className="font-montserrat text-black text-base font-bold !my-auto lg:ml-10 lg:mr-10 max-sm:ml-4 md:max-lg:px-10 max-sm:mr-4">
                          Medical & Lifestyle
                        </span>
                        <div>
                          {showNewVersionUW(jointMembersData) ? (
                            <JointLifeMedicalV2 />
                          ) : (
                            <MedicalAndLifestyle
                              questions={medicalQuestions}
                              displayAdditionalQue={toDisplayJointLifeAdditionalQue}
                              label="Joint Life"
                              isReview
                              jointMarginRequired
                              formValues={JointMedicalAndLifeStyle}
                              setFieldValue={formik.setFieldValue}
                              handleBlurs={formik.handleBlur}
                              handleChanges={formik.handleChange}
                              setFieldTouch={formik.setFieldTouched}
                              width="78"
                              gender={jointGender}
                            />
                          )}
                        </div>
                        {nationalityJointMember !== "RES" && (
                          <div className="ml-10 mb-6 mr-6">
                            <div className="flex justify-between pt-10">
                              <div className="text-base font-bold">NRI Details</div>
                            </div>
                            <JointCovidAndNriReview />
                          </div>
                        )}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}
            {[EInsurerType.ALL, EInsurerType.PRIMARY]?.includes(insurerType) && (
              <div className="mt-6 border border-solid rounded-xl border-bianchiGreen">
                <Accordion
                  className="!rounded-xl !shadow-none"
                  expanded={expanded === "panel7"}
                  onChange={handleChange("panel7")}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        className=" rounded-full border-2 border-coldMorning"
                        classes={{ root: "!p-1" }}
                      />
                    }
                    aria-controls="panel6bh-content"
                    id="panel7bh-header"
                  >
                    <Typography className="!text-base !font-bold">Loan & Plan Details</Typography>
                  </AccordionSummary>
                  <hr className="border-solid border-black opacity-30" />
                  <AccordionDetails>
                    <Typography>
                      <PlanReview />
                      <PlanDetailsReview />
                      <PremiumDetailsReview />
                      <PremiumFundingReview />
                      {loanDetails.existingPolicy.existingLifeCover && <PaymentMethodReview />}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </div>
            )}
            {assignmentStepperFlag && (
              <div className="mt-6 border border-solid rounded-xl border-bianchiGreen">
                <Accordion
                  className="!rounded-xl !shadow-none"
                  expanded={expanded === "panel8"}
                  onChange={handleChange("panel8")}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        className=" rounded-full border-2 border-coldMorning"
                        classes={{ root: "!p-1" }}
                      />
                    }
                    aria-controls="panel6bh-content"
                    id="panel7bh-header"
                  >
                    <Typography className="!text-base !font-bold">Assignment Notice</Typography>
                  </AccordionSummary>
                  <hr className="border-solid border-black opacity-30" />
                  <AccordionDetails>
                    <Typography>
                      <AssignmentNotice />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProposalOtp;
